<template>
    <!-- Page breadcrumb + title header. Self-contained and reusable:
         Data can come from simple props (navInformation / title / description) OR
         from slots (#title, #description, #icon, #actions). Slots win when present,
         so a blade page can pass plain strings while a Vue page can render rich
         content. Every slot is optional. -->
    <div class="w-full pt-4 md:pt-0">
        <nav>
            <!-- Breadcrumb row, with the optional #actions slot pinned right (inline
                 with the crumbs, so actions never interrupt the title/description
                 below). The min-height holds the row at the action button's height
                 whether or not a button is present, so the title does not shift when
                 an action toggles between views (e.g. "How it works"). Explicit px,
                 not min-h-8: this app sets html { font-size: 14px }, so 2rem would
                 resolve to 28px -- under the ~30px button -- and the row would grow. -->
            <div    v-if="hasBreadcrumb || $slots.actions"
                    class="flex min-h-[34px] items-center justify-between gap-3">
                <ol v-if="hasBreadcrumb"
                    class="m-0 flex min-w-0 items-center whitespace-nowrap p-0">
                    <li v-for="(link, text, index) in navInformation.links"
                        :key="index"
                        class="inline-flex items-center">
                        <a :href="link"
                           class="flex items-center text-sm text-gray-500 hover:text-blue-600 focus:text-blue-600 focus:outline-none plain-link">
                            {{ text }}
                        </a>
                        <svg class="mx-2 shrink-0 text-gray-400 size-4"
                             xmlns="http://www.w3.org/2000/svg"
                             width="24"
                             height="24"
                             viewBox="0 0 24 24"
                             fill="none"
                             stroke="currentColor"
                             stroke-width="2"
                             stroke-linecap="round"
                             stroke-linejoin="round">
                            <path d="m9 18 6-6-6-6"></path>
                        </svg>
                    </li>

                    <li class="inline-flex items-center truncate text-sm font-semibold text-gray-800"
                        aria-current="page">
                        {{ navInformation.end }}
                    </li>
                </ol>
                <!-- Keep the actions pinned right even with no breadcrumb. -->
                <span v-else></span>

                <div    v-if="$slots.actions"
                        class="flex shrink-0 items-center gap-2">
                    <slot name="actions"></slot>
                </div>
            </div>

            <!-- Title row: an optional leading icon tile (blue rounded square,
                 matching the report page header) beside the <h1>. The icon comes
                 from the #icon slot; the title from the #title slot, else the
                 `title` prop, else the breadcrumb's final crumb. -->
            <div    :class="{'mt-2': showBreadcrumbs || $slots.actions}"
                    class="flex items-center gap-2 sm:gap-3">
                <div    v-if="$slots.icon"
                        class="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-blue-500 text-white">
                    <slot name="icon"></slot>
                </div>

                <!-- Title notches down one step on a phone (text-2xl) so a long
                     report name does not dominate the narrow header, and returns
                     to text-3xl from sm up. -->
                <h1 class="m-0 p-0 text-left text-2xl font-bold tracking-tight text-gray-700 sm:text-3xl">
                    <slot name="title">{{ resolvedTitle }}</slot>
                </h1>
            </div>

            <!-- Optional description under the title. From the #description slot,
                 else the `description` prop. Omitted entirely when neither is
                 supplied. -->
            <div    v-if="$slots.description || description"
                    class="m-0 mt-1 text-left text-sm text-gray-600 sm:text-base">
                <slot name="description">{{ description }}</slot>
            </div>
        </nav>
    </div>
</template>

<script>
export default {
    name: "TitleNav",
    props: {
        // Breadcrumb source: { links: { "Text": "url", ... }, end: "Current" }.
        // Optional -- a TitleNav can render just a title with no breadcrumb.
        navInformation: {
            type: Object,
            default: null,
        },
        // Override for the <h1>. The breadcrumb's final crumb still comes from
        // navInformation.end, so a page can keep a stable breadcrumb
        // ("Reports > Excursion Report") while the heading names the current view
        // ("Group Templates"). Falls back to navInformation.end. The #title slot
        // beats both.
        title: {
            type: String,
            default: null,
        },
        // One-line lead-in under the title. The #description slot beats it.
        description: {
            type: String,
            default: null,
        },
        // Pages that already show these breadcrumbs in the top nav can hide the copy here.
        showBreadcrumbs: {
            type: Boolean,
            default: true,
        },
    },
    computed: {
        // A breadcrumb renders only when nav data with a final crumb is present
        // and the page has not hidden it.
        hasBreadcrumb() {
            return this.showBreadcrumbs && !! (this.navInformation && this.navInformation.end)
        },
        // #title slot > title prop > breadcrumb end > empty.
        resolvedTitle() {
            return this.title ?? this.navInformation?.end ?? ''
        },
    },
}
</script>
